import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { IUnitProps } from "./Unit.interface";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { useGetModule, useGetUnit } from "../../shared/hooks/useModules";
import { IModule, IModuleUnit } from "../../shared/interfaces/modules.interface";
import { routes } from "../../shared/router/routes";
import ComponentPicker from "../../components/UnitComponents/ComponentPicker/ComponentPicker";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import { CONTENT_BASE_URL, DEFAULT_LANGUAGE } from "../../shared/constants";
import UnitCard from "../../components/UnitCard/UnitCard";
import Placeholder from "./../../shared/assets/imgs/placeholder.svg";
import Link from "../../components/Link/Link";
import { AuthenticationContext } from "../../shared/providers/authentication/authentication.provider";
import { AnalyticsContext } from "../../shared/providers/analytics/analytics.provider";
import { LayoutContext } from "../../shared/providers/layout/layout.provider";
import { getFormattedPath } from "../../shared/helpers/utils";

function Unit(props: IUnitProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { lang, translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { user } = useContext(AuthenticationContext);
    const { setEvent } = useContext(AnalyticsContext);
    const { setActions } = useContext(LayoutContext);
    const { modules, module, setModule, setCurrentUnitId, setUpdated } = useContext(ModuleContext);

    const { unitId: id, moduleId } = useParams();
    const { isLoading, data: unit } = useGetUnit<IModuleUnit>(id, user);
    const { data: moduleData } = useGetModule<IModule>(moduleId, lang ?? DEFAULT_LANGUAGE, user);
    const [nextModule, setNextModule] = useState<IModule | undefined>();
    const fromExplore = !!(location.state as { fromExplore?: boolean })?.fromExplore;

    const nextIndex = useMemo(
        () => (module?.units?.findIndex((unit) => unit.id === id) ?? 0) + 1,
        [module, id]
    );
    const previousIndex = useMemo(
        () => (module?.units?.findIndex((unit) => unit.id === id) ?? 0) - 1,
        [module, id]
    );
    const hasNextUnit = useMemo(() => module && module.units?.[nextIndex], [nextIndex, module]);
    const hasPreviousUnit = useMemo(
        () => module && module.units?.[previousIndex],
        [previousIndex, module]
    );

    useEffect(() => {
        if (!modules || !module) return;

        const currentModuleIndex = modules.findIndex((mod) => mod.id === module.id);
        const nextModule = modules[currentModuleIndex + 1];

        if (nextModule && (!nextModule.moduleProgress || nextModule.moduleProgress < 100)) {
            // HAS A NEXT UNCOMPLETED MODULE
            setNextModule(nextModule);
        }
        else {
            // FINDS THE NEXT UNCOMPLETED MODULE
            const uncompletedModule = modules.find(mod => !mod.moduleProgress || mod.moduleProgress < 100);
            setNextModule(uncompletedModule);
        }
    }, [module, modules]);

    useEffect(() => {
        if (!user) return;

        if (fromExplore) {
            setEvent({
                event_name: "app_explore_unit",
                event_category: "content_navigation",
                event_action: "app_explore_unit",
                user_token: user?.id,
                company_id: user?.company.id,
                module_id: moduleId,
                unit_id: id
            });
            return;
        }

        setEvent({
            event_name: "app_module_unit",
            event_category: "content_navigation",
            event_action: "app_module_unit",
            user_token: user?.id,
            company_id: user?.company.id,
            module_id: moduleId,
            unit_id: id
        });
    }, [user, id, fromExplore]);

    useEffect(() => {
        setIsLoading(isLoading || !module);

        if (isLoading) return;

        if (!unit) navigate(routes.HOME.path);

        document.body.scrollIntoView({ behavior: "smooth" })
    }, [isLoading, module, navigate, unit]);

    useEffect(() => {
        if (module) return;

        setModule(moduleData);
    }, [module, moduleData]);

    useEffect(() => {
        setCurrentUnitId(id);
        // UPDATE MODULES PROGRESS WHEN ENTERING UNIT
        setUpdated(new Date());

        return () => {
            // UPDATE MODULES PROGRESS WHEN EXITING UNIT PAGE
            setUpdated(new Date());
        }
    }, [id]);

    useEffect(() => {

        setActions(<Link
            to={getFormattedPath(routes.MODULE.path, { moduleId: moduleId })}
            icon="Chevron--left"
            iconSize={16}
            customCss="p-0 ms-lg-2 d-flex align-items-center gap-01 color-neutrals-black text-bd2-rg">
            {translate("common.back", "Back")}
        </Link>)

        return () => {
            setActions(undefined)
        }
    }, [setActions, translate, getFormattedPath, moduleId]);

    return (
        <>
            <div className="container mx-auto page_unit row pb-md-6 px-1 px-md-2 px-lg-0">

                <div className="unit d-flex flex-column h-100">
                    <div className="d-flex flex-column gap-1 mb-1">
                        <div className="row gx-0">
                            <div className="col-12 d-flex flex-column gap-02">
                                <span className="text-bd3-rg color-neutrals-dark">
                                    Unit {nextIndex}
                                </span>
                                <h1 className="text-h2-smbd color-neutrals-black mb-02">
                                    {unit?.title}
                                </h1>
                                <p className="text-bd1-rg color-neutrals-black m-0">
                                    {unit?.description}
                                </p>
                            </div>
                        </div>
                        {unit?.items.map((item, i) => (
                            <ComponentPicker key={i} unitId={id} {...item}></ComponentPicker>
                        ))}
                    </div>
                    <div className="row gx-1 g-md-12 mt-md-6">
                        {hasPreviousUnit && (
                            <div className={`${hasNextUnit ? "col-md-6" : ""} col-12`}>
                                <div className="unit_next bg-blue-50 px-1 pt-22 pt-md-2 pb-12 mx-n1 mx-md-0 h-100">
                                    <h2 className="text-h4-smbd mb-12">
                                        {translate(
                                            "modules.previous_module_unit",
                                            "Previous module unit"
                                        )}
                                    </h2>
                                    <UnitCard
                                        moduleId={module!.id}
                                        moduleImage={
                                            module!.image?.url
                                                ? `${CONTENT_BASE_URL}${module!.image?.url}`
                                                : Placeholder
                                        }
                                        unit={module!.units?.[previousIndex] as IModuleUnit}
                                        index={previousIndex + 1}
                                        fromUnit
                                    />
                                </div>
                            </div>
                        )}
                        {hasNextUnit && (
                            <div className={`${hasPreviousUnit ? "col-md-6" : ""} col-12`}>
                                <div className="unit_next bg-blue-50 px-1 pt-22 pt-md-2 pb-12 mx-n1 mx-md-0 h-100">
                                    <h2 className="text-h4-smbd mb-12">
                                        {translate(
                                            "modules.next_module_unit",
                                            "Next module unit"
                                        )}
                                    </h2>
                                    <UnitCard
                                        moduleId={module!.id}
                                        moduleImage={
                                            module!.image?.url
                                                ? `${CONTENT_BASE_URL}${module!.image?.url}`
                                                : Placeholder
                                        }
                                        unit={module!.units?.[nextIndex] as IModuleUnit}
                                        index={nextIndex + 1}
                                        fromUnit
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    {!hasNextUnit && (
                        <div className="unit_action row pb-1 mx-n1 mx-md-0 px-1 px-md-0 pt-md-2">
                            <div className="col-12 col-md-6">
                                <Link
                                    variant="solid"
                                    customCss="w-100"
                                    to={routes.EXPLORE.path}>
                                    {translate("modules.conclude_module", "Conclude module")}
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default Unit;
